import React, { useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import moment from 'moment';
import { FiClock, FiTruck, FiExternalLink, FiInbox } from 'react-icons/fi';
import { useDispatch } from 'react-redux';

import { FetchRestockSkuPoBreakdown } from '../../slices/restock';

/*
 * PO breakdown modal for the "Pending on PO" and "WH Inbound" cells — shared across the Restock, SKU,
 * Products, Database, and Catalog pages. Shows the purchase-order lines behind the number (Created Date
 * / PO Name / Qty / Unit Cost); the PO name links to its detail page in a new tab.
 *   `params` — what to fetch by: { sku_id } (SKU/restock rows) or { upc } (product/catalog/db rows).
 *   `label`  — subtitle shown under the title (the SKU string or UPC).
 *   `type`   — 'pending' | 'inbound' selects which list to display.
 *
 * Layout: compact header, and a card-style table whose body always reserves exactly FIVE rows —
 * fewer rows leave blank space (stable footprint), more rows scroll under the sticky header.
 * Zebra striping + hover live in assets/style.css (.restock-breakdown-row).
 */

// Fixed row math so the body height is exactly header + 5 rows.
const HEAD_H = 33; // 16px line + 2*8px padding + 1px border
const ROW_H = 39; // 18px line + 2*10px padding + 1px border
const BODY_H = HEAD_H + ROW_H * 5;

export default function PoBreakdownModal({ open, onClose, params, label, type }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ pending: [], inbound: [] });

  // Stable primitive key so a fresh `params` object literal each render doesn't retrigger the fetch.
  const paramKey = params ? (params.sku_id != null ? `s:${params.sku_id}` : `u:${params.upc}`) : null;

  useEffect(() => {
    if (!open || !paramKey || !params) return undefined;
    let alive = true;
    setLoading(true);
    dispatch(FetchRestockSkuPoBreakdown(params)).unwrap()
      .then((res) => { if (alive) setData(res?.data || { pending: [], inbound: [] }); })
      .catch(() => { if (alive) setData({ pending: [], inbound: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paramKey, dispatch]);

  const isInbound = type === 'inbound';
  const rows = (isInbound ? data.inbound : data.pending) || [];
  const accent = isInbound ? '#039855' : '#1570EF';
  const accentSoft = isInbound ? '#ECFDF3' : '#EFF8FF';

  const th = { textAlign: 'left', fontSize: 11, lineHeight: '16px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 16px', background: '#F9FAFB', borderBottom: '1px solid #EAECF0', position: 'sticky', top: 0, zIndex: 1 };
  const thR = { ...th, textAlign: 'right' };
  const td = { fontSize: 13, lineHeight: '18px', color: '#101828', padding: '10px 16px', borderBottom: '1px solid #F2F4F7' };
  const tdR = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      footer={null}
      width={780}
      centered
      title={null}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
      zIndex={100000}
      className="restock-breakdown-modal"
      maskStyle={{ background: 'rgba(16, 24, 40, 0.45)' }}
    >
      {/* Header */}
      <div style={{ padding: '10px 15px', borderBottom: '1px solid #F2F4F7' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: accentSoft, color: accent, flexShrink: 0 }}>
            {isInbound ? <FiTruck size={17} /> : <FiClock size={17} />}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1B2559', lineHeight: 1.25 }}>{isInbound ? 'WH Inbound' : 'Pending on PO'} breakdown</div>
            <div style={{ fontSize: 12, color: '#98A2B3', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 620 }}>
              {label || ''}
            </div>
          </div>
        
        </div>
      </div>

      {/* Table card — fixed footprint: always exactly 5 rows tall; >5 rows scroll, <5 leave blank space */}
      <Spin spinning={loading}>
        <div style={{ margin: '14px 20px 20px', border: '1px solid #EAECF0', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ height: BODY_H, overflowY: 'auto', overflowX: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 130 }}>Created Date</th>
                  <th style={th}>PO Name</th>
                  <th style={{ ...thR, width: 90 }}>Qty</th>
                  <th style={{ ...thR, width: 110 }}>Unit Cost</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={4} style={{ height: ROW_H * 5 - 1, textAlign: 'center', verticalAlign: 'middle', borderBottom: 'none' }}>
                    <span style={{ width: 40, height: 40, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#F2F4F7', color: '#98A2B3', marginBottom: 8 }}>
                      <FiInbox size={18} />
                    </span>
                    <div style={{ fontSize: 13, color: '#98A2B3' }}>
                      No {isInbound ? 'incoming' : 'pending'} purchase orders for this SKU.
                    </div>
                  </td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={`${r.po_id}-${r.upc}-${i}`} className="restock-breakdown-row">
                    <td style={{ ...td, color: '#667085', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.created_at ? moment(r.created_at).format('MMM DD, YYYY') : 'N/A'}</td>
                    <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
                      {/* Open the PO detail page in a new tab. */}
                      <a
                        href={`/po-details/${r.po_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 600, color: '#1565D8', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      >
                        {r.po_name || 'N/A'}
                        <FiExternalLink size={11} style={{ opacity: 0.65, flexShrink: 0 }} />
                      </a>
                      {r.upc ? <span style={{ color: '#98A2B3', fontSize: 11, marginLeft: 6 }}>· {r.upc}</span> : null}
                    </td>
                    <td style={tdR}>
                      <span style={{ display: 'inline-block', minWidth: 44, padding: '1px 10px', borderRadius: 999, background: accentSoft, color: accent, fontWeight: 600, fontSize: 12.5 }}>
                        {Number(r.qty).toLocaleString()}
                      </span>
                    </td>
                    <td style={{ ...tdR, color: '#344054' }}>${Number(r.unit_cost || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Spin>
    </Modal>
  );
}
